import React from 'react';
import { Tooltip } from 'antd';

type SidebarIcon = React.ComponentType<{ className?: string }>;

export type SidebarNavItemProps = {
  label: string;
  icon: SidebarIcon;
  selected?: boolean;
  collapsed?: boolean;
  count?: number;
  labelBadge?: React.ReactNode;
  trailing?: React.ReactNode;
  cornerBadge?: React.ReactNode;
  onClick: () => void;
  ariaCurrent?: 'page';
  ariaExpanded?: boolean;
};

const formatCount = (count: number) => (count > 99 ? '99+' : count);

export default function SidebarNavItem({
  label,
  icon: Icon,
  selected = false,
  collapsed = false,
  count = 0,
  labelBadge,
  trailing,
  cornerBadge,
  onClick,
  ariaCurrent,
  ariaExpanded
}: SidebarNavItemProps) {
  const iconClassName = `h-5 w-5 shrink-0 ${
    selected
      ? 'text-[var(--creator-sidebar-text-muted)]'
      : 'text-[var(--creator-sidebar-icon)]'
  }`;
  const countBadge = count > 0 && (
    <span className="flex h-[22px] min-w-[22px] shrink-0 items-center justify-center rounded-full bg-[var(--creator-sidebar-unread)] px-1 font-['DM_Sans'] text-sm font-semibold leading-5 text-white">
      {formatCount(count)}
    </span>
  );

  const button = (
    <button
      type="button"
      aria-label={label}
      aria-current={ariaCurrent}
      aria-expanded={ariaExpanded}
      onClick={onClick}
      className={`group relative flex h-10 cursor-pointer items-center border-0 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FB6011] ${
        collapsed
          ? 'w-10 justify-center px-0'
          : 'w-full justify-between px-[10px]'
      } ${
        selected
          ? 'rounded-lg bg-[var(--creator-sidebar-selected)]'
          : 'rounded-md bg-transparent hover:bg-[var(--creator-sidebar-surface)]'
      }`}
    >
      <span className="flex min-w-0 items-center gap-2">
        {collapsed && count > 0 ? (
          <>
            <span className="flex h-[22px] min-w-[22px] shrink-0 items-center justify-center rounded-full bg-[var(--creator-sidebar-unread)] px-1 font-['DM_Sans'] text-sm font-semibold leading-5 text-white group-hover:hidden group-focus-visible:hidden">
              {formatCount(count)}
            </span>
            <Icon
              className={`${iconClassName} hidden group-hover:block group-focus-visible:block`}
            />
          </>
        ) : (
          <Icon className={iconClassName} />
        )}
        {!collapsed && (
          <span
            className={`truncate font-['Inter'] text-sm leading-5 ${
              selected
                ? 'font-semibold text-[var(--creator-sidebar-nav-text-selected)]'
                : 'font-medium text-[var(--creator-sidebar-nav-text)]'
            }`}
          >
            {label}
          </span>
        )}
        {!collapsed && labelBadge}
      </span>
      {!collapsed && (countBadge || trailing)}
      {cornerBadge}
    </button>
  );

  if (!collapsed) return button;

  return (
    <Tooltip title={label} placement="right">
      {button}
    </Tooltip>
  );
}
